import { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { api } from '../lib/api';
import { useAuth } from './AuthContext';

const NotificationContext = createContext(null);

const POLL_INTERVAL_MS = 30000;

export const useNotifications = () => {
  const ctx = useContext(NotificationContext);
  if (!ctx) throw new Error('useNotifications must be used within a NotificationProvider');
  return ctx;
};

export const NotificationProvider = ({ children }) => {
  const { user, token } = useAuth();
  const [items, setItems] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const seenIdsRef = useRef(new Set());
  const pollRef = useRef(null);
  const browserPermissionRef = useRef(false);

  const fireBrowserNotification = useCallback((notif) => {
    if (!browserPermissionRef.current) return;
    if (typeof window === 'undefined' || !('Notification' in window)) return;
    if (Notification.permission !== 'granted') return;
    try {
      const n = new Notification('Fundfair · ' + notif.title, {
        body: notif.body,
        tag: notif.id,
      });
      n.onclick = () => {
        window.focus();
        if (notif.link) window.location.href = notif.link;
      };
    } catch (e) {
      // swallow
    }
  }, []);

  const refresh = useCallback(async ({ silent = false } = {}) => {
    if (!token || !user) return;
    if (!silent) setLoading(true);
    try {
      const res = await api.get('/notifications', { params: { limit: 20 } });
      const data = res.data || {};
      const list = data.items || [];
      // Detect new notifications and surface as browser notifications
      const prevSeen = seenIdsRef.current;
      list.forEach((n) => {
        if (!prevSeen.has(n.id) && !n.read) {
          fireBrowserNotification(n);
        }
      });
      seenIdsRef.current = new Set(list.map((n) => n.id));
      setItems(list);
      setUnreadCount(data.unread_count || 0);
    } catch (e) {
      // silent fail; the bell will simply not update
    } finally {
      if (!silent) setLoading(false);
    }
  }, [token, user, fireBrowserNotification]);

  const refreshCount = useCallback(async () => {
    if (!token || !user) return;
    try {
      const res = await api.get('/notifications/unread-count');
      setUnreadCount(res.data?.unread_count || 0);
    } catch (e) { /* ignore */ }
  }, [token, user]);

  // Initial load + polling
  useEffect(() => {
    if (!token || !user) {
      setItems([]);
      setUnreadCount(0);
      seenIdsRef.current = new Set();
      return undefined;
    }
    refresh({ silent: true });
    pollRef.current = setInterval(() => refresh({ silent: true }), POLL_INTERVAL_MS);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [token, user, refresh]);

  // Track browser permission state
  useEffect(() => {
    if (typeof window !== 'undefined' && 'Notification' in window) {
      browserPermissionRef.current = Notification.permission === 'granted';
    }
  }, []);

  const requestBrowserPermission = useCallback(async () => {
    if (typeof window === 'undefined' || !('Notification' in window)) {
      return 'unsupported';
    }
    if (Notification.permission === 'granted') {
      browserPermissionRef.current = true;
      return 'granted';
    }
    if (Notification.permission === 'denied') {
      return 'denied';
    }
    try {
      const result = await Notification.requestPermission();
      browserPermissionRef.current = result === 'granted';
      return result;
    } catch (e) {
      return 'error';
    }
  }, []);

  const markAsRead = useCallback(async (id) => {
    try {
      await api.post(`/notifications/${id}/read`);
      setItems((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
      setUnreadCount((c) => Math.max(0, c - 1));
    } catch (e) { /* ignore */ }
  }, []);

  const markAllAsRead = useCallback(async () => {
    try {
      await api.post('/notifications/read-all');
      setItems((prev) => prev.map((n) => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (e) { /* ignore */ }
  }, []);

  const deleteOne = useCallback(async (id) => {
    try {
      await api.delete(`/notifications/${id}`);
      setItems((prev) => prev.filter((n) => n.id !== id));
      refreshCount();
    } catch (e) { /* ignore */ }
  }, [refreshCount]);

  const clearAll = useCallback(async () => {
    try {
      await api.delete('/notifications');
      setItems([]);
      setUnreadCount(0);
    } catch (e) { /* ignore */ }
  }, []);

  const value = {
    items,
    unreadCount,
    loading,
    refresh,
    refreshCount,
    markAsRead,
    markAllAsRead,
    deleteOne,
    clearAll,
    requestBrowserPermission,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};
